import type { ActiveGameStore } from "./ActiveGameStore";
import type { SchemaVersioned } from "./types";

// Shared lifecycle for the in-flight session: own a single TSession (or null),
// adopt an existing one (resume path), persist on every mutation, drop on
// discard. Subclasses provide the per-game `start(...)` and recording APIs —
// the only contract they must respect is calling `setSession` once after
// construction and `updateSession` for every mutation thereafter so the
// localStorage slot stays current.
export abstract class BaseGameRecorder<TSession extends SchemaVersioned> {
  protected session: TSession | null = null;
  protected readonly store: ActiveGameStore<TSession>;

  constructor(store: ActiveGameStore<TSession>) {
    this.store = store;
  }

  adopt(session: TSession): void {
    this.session = session;
  }

  discard(): void {
    this.session = null;
    this.store.clear();
  }

  getSession(): TSession | null {
    return this.session;
  }

  // Initialise a fresh session and persist it. Subclasses call this from their
  // `start(seed)` after assembling the per-game session shape.
  protected setSession(session: TSession): void {
    this.session = session;
    this.store.save(session);
  }

  // Apply a pure transform to the current session and persist the result.
  // No-ops when no session is active so subclasses don't need to null-check.
  protected updateSession(fn: (session: TSession) => TSession): void {
    if (!this.session) return;
    this.session = fn(this.session);
    this.store.save(this.session);
  }
}
